// @noflow
import { countries } from 'countries-list'
import i18next from 'i18next'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js/min'
import * as React from 'react'
import { ChangeEvent, ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import * as Validation from '@/components/pages/CheckoutPage/components/Validation/Validation'

import type { Code as CountryCode } from '@/shared_types/rails_models/shipping_countries'

import {
  mapFieldNameToPlaceholder,
  validationIndicator
} from '../../helpers/inputs'
import { BaseInputField, InputWithString } from './InputField'

type Props = {
  value: InputWithString
  onBlur: (event: ChangeEvent<HTMLInputElement>) => void
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  shouldSeeRequiredPhoneNumber: boolean
  shippingCountryCode: CountryCode
  dataTestId?: string
}

/**
 * Validates a mobile number based on the provided parameters.
 * @param value - The mobile number to validate.
 * @param shouldSeeRequiredPhoneNumber - Indicates whether the phone number is required.
 * @param shippingCountryCode - The country code for the shipping address.
 * @returns A string containing an error message if the mobile number is invalid, or null if it is valid.
 */
const validateMobileNumber = ({
  value,
  shouldSeeRequiredPhoneNumber,
  shippingCountryCode
}: {
  value: string
  shouldSeeRequiredPhoneNumber: boolean
  shippingCountryCode: CountryCode
}): string | null => {
  const copy_context = 'checkout:errors'

  const supportedCountryNumbers = Object.entries(countries)
    .filter(([key, { continent }]) => continent === 'EU' || key === 'US')
    .map(([key]) => key)

  // If there is no value, we need to check if the field is required
  // according to a feature flag
  if (!value) {
    if (shouldSeeRequiredPhoneNumber) {
      return i18next.t(`${copy_context}.mobile`)
    }
    return null
  }

  // Remove whitespace from the number
  const trimmedNumber = value.trim()

  // For Northen Ireland we need to use GB as the country code
  const sanitisedShippingCountryCode =
    shippingCountryCode === 'NI' ? 'GB' : shippingCountryCode

  // We check first that the number is a valid complete mobile number, because parsePhoneNumber
  // requires a complete phone number otherwise it'll error on attempting to parse
  const isValidMobileNumber = isValidPhoneNumber(
    trimmedNumber,
    sanitisedShippingCountryCode
  )

  // If the number is not valid, we return an error message
  if (!isValidMobileNumber) {
    return i18next.t(`${copy_context}.mobile`)
  }

  // We have a defined list of allowed European + US phone numbers which we want to check against
  const parsedNumberCountryCode = parsePhoneNumber(
    trimmedNumber,
    sanitisedShippingCountryCode
  ).country

  if (!parsedNumberCountryCode) return null

  const isSupportedCountryNumber = supportedCountryNumbers.includes(
    parsedNumberCountryCode
  )

  if (!isSupportedCountryNumber) {
    return i18next.t(`${copy_context}.mobile`)
  }

  return null
}

/**
 * MobileNumberInputField
 *
 * This component renders an input field specifically for mobile numbers.
 * It also includes validation logic and handles changes to the input value.
 *
 * @component
 * @param {Object} props - The props object.
 * @param {Object} props.value - The current value of the input field.
 * @param {string} props.dataTestId - The data test ID for the input field.
 * @param {Function} props.onBlur - The onBlur callback function.
 * @param {Function} props.onChange - The onChange callback function.
 * @param {boolean} props.shouldSeeRequiredPhoneNumber - Indicates if the phone number is required.
 * @param {string} [props.shippingCountryCode] - The Users ShippingCountry code.
 * @returns {ReactElement} The rendered component.
 *
 * @example
 * // To use this component, import it and use it in your React component's render method:
 * import MobileNumberInputField from './MobileNumberInputField';
 *
 * function MyComponent() {
 *   const [mobileNumber, setMobileNumber] = useState('');
 *
 *   return (
 *     <MobileNumberInputField
 *       value={mobileNumber}
 *       onBlur={() => console.log('Input field lost focus')}
 *       onChange={(e) => setMobileNumber(e.target.value)}
 *       shouldSeeRequiredPhoneNumber={true}
 *       shippingCountryCode="GB"
 *       dataTestId="checkout-mobile-number-input"
 *     />
 *   );
 * }
 */
const MobileNumberInputField = ({
  value,
  dataTestId,
  onBlur,
  onChange,
  shouldSeeRequiredPhoneNumber,
  shippingCountryCode
}: Props): ReactElement => {
  const { t } = useTranslation('checkout')
  const copy_context = 'input_placeholders'

  const indicator = validationIndicator(value)

  const onChangeCallback = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event)
      validateMobileNumber({
        value: event.currentTarget.value,
        shouldSeeRequiredPhoneNumber,
        shippingCountryCode
      })
    },
    [onChange, shippingCountryCode, shouldSeeRequiredPhoneNumber]
  )

  // If a value is already present, validate it on
  // component load and set the state of the password
  // rules appropriately
  useEffect(() => {
    if (value.inputInteractionState === 'NotInteractingInteracted') {
      validateMobileNumber({
        value: value.value,
        shouldSeeRequiredPhoneNumber,
        shippingCountryCode
      })
    }
  }, [
    value.value,
    value.inputInteractionState,
    shouldSeeRequiredPhoneNumber,
    shippingCountryCode
  ])

  return (
    <>
      <BaseInputField
        fieldName={'mobileNumber'}
        isValid={indicator !== 'error'}
        onBlur={onBlur}
        onChange={onChangeCallback}
        dataTestId={dataTestId}
        value={value.value}
        shippingCountryCode={shippingCountryCode}
      >
        {indicator === 'checkmark' ? (
          <Validation.Checkmark color="green" />
        ) : null}

        <p
          className={`checkout-placeholder mobileNumber-placeholder`}
          id={`mobileNumber-placeholder`}
        >
          {mapFieldNameToPlaceholder(t, 'mobileNumber', shippingCountryCode)}

          {shouldSeeRequiredPhoneNumber ? null : (
            <span className="optional-label">
              {`- ${t(`${copy_context}.optional`)}`}
            </span>
          )}
        </p>
      </BaseInputField>
      {indicator === 'error' && (
        <Validation.Error errorMessage={value.errorMessage || ''} />
      )}
    </>
  )
}

export default MobileNumberInputField

export { validateMobileNumber }
